import { adContextSchema, saleSourceSchema } from "@libs/attribution-contracts";
import { z } from "zod";
import { accessLineupDisplaySchema, accessLineupSchema } from "./access-lineup";
import {
  artistLinkPageSlugSchema,
  artistLinkPageSongPlatformLinkSchema,
  eventKindSchema,
  eventStartLocalSchema,
} from "@libs/link-in-bio-config";
import { purchaseSchema } from "./purchase";
import { collectOrNullSchema } from "./collect";

export const linkPageAccessNotFoundSchema = z.object({
  error: z.literal("link_page_access_not_found"),
  slug: z.string(),
  accessId: z.string(),
});

export const linkPageAccessUnavailableSchema = z.object({
  error: z.literal("link_page_access_unavailable"),
  reason: z.enum([
    "not_checkoutable",
    "sold_out",
    "external_ticketing",
    "invalid_ticket_count",
  ]),
});

const linkPageAccessArtistSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  imageUrl: z.string().url().nullable(),
});

export const buyerDetailRuleSchema = z.enum(["off", "optional", "required"]);
export type BuyerDetailRule = z.infer<typeof buyerDetailRuleSchema>;

export const linkPageEventAccessSchema = z.object({
  slug: artistLinkPageSlugSchema,
  artist: linkPageAccessArtistSchema,
  access: z.object({
    lineup: accessLineupDisplaySchema.optional(),
    engineEventId: z.string().nullable().optional(),
    /**
     * The engine's hosted checkout page this event's sessions open on
     * (`…/checkout/{organizer}`), with no session in it: the tickets sheet
     * loads the checkout from it before the session exists. Absent or null
     * when it is not known; the sheet then loads it with the session.
     */
    engineCheckoutPage: z.string().url().nullable().optional(),
    engineTiers: z
      .array(
        z.object({
          engineItemId: z.string(),
          name: z.string(),
          priceMinor: z.number(),
          currency: z.string(),
          saleStartsAt: z.string().nullable(),
          saleEndsAt: z.string().nullable(),
          // LIS-912. Optional so a page cached before the field existed
          // still parses: absent is false.
          saleAwaitingTrigger: z.boolean().optional(),
          soldOut: z.boolean(),
          gated: z.boolean(),
          active: z.boolean(),
          // LIS-1000. Optional so a page cached before the fields existed
          // still parses.
          requiresApproval: z.boolean().optional(),
          maxPerOrder: z.number().int().positive().nullable().optional(),
        }),
      )
      .optional(),
    /** What the organiser asks of a fan beyond their email (LIS-1000). */
    buyerNameRule: buyerDetailRuleSchema.optional(),
    buyerPhoneRule: buyerDetailRuleSchema.optional(),
    /** The engine event's per-order ticket limits, counted across all its
     *  tiers (LIS-924); null or absent: no limit. The tickets sheet bounds
     *  its steppers by them; the engine refuses a checkout outside them. */
    minTicketsPerOrder: z.number().int().positive().nullable().optional(),
    maxTicketsPerOrder: z.number().int().positive().nullable().optional(),
    type: z.literal("event"),
    id: z.string().min(1),
    title: z.string().min(1),
    kind: eventKindSchema.nullable().optional(),
    description: z.string().nullable(),
    /** At-the-door notes (age policy, entrance, ID), same subset as description. */
    instructions: z.string().nullable().optional(),
    // start INSTANT — logic only; display comes from startsAtLocal (venue-local
    // components resolved server-side — clients do zero timezone work).
    startsAt: z.string().datetime(),
    startsAtLocal: eventStartLocalSchema,
    endsAt: z.string().datetime().nullable(),
    /** Set once the event is cancelled; `checkoutMode` is then unavailable. */
    cancelledAt: z.string().datetime().optional(),
    // READ schema (2026-07-22, same reasoning as artistLinkPageAccessSchema):
    // converger-created accesses legitimately lack venue/city/country — empty is
    // accepted and render layers guard/omit.
    city: z.string(),
    venue: z.string(),
    /** Full street address when the organiser entered one; null otherwise. */
    address: z.string().nullable().optional(),
    /** Section order: Location above the Line-up when true. */
    locationFirst: z.boolean().optional(),
    country: z.string(),
    imageUrl: z.string().url().nullable(),
    externalTicketUrl: z.string().url().nullable(),
    /** The page asks for the visitor's email before the external ticket link. */
    collectEmail: z.boolean().optional(),
    checkoutMode: z.enum(["external", "free", "paid", "unavailable"]),
    price: z
      .object({
        amount: z.number().nonnegative(),
        currency: z.string().min(1),
        /** The lowest of several ticket prices: render as "From …". */
        from: z.boolean().optional(),
      })
      .nullable(),
    availableTicketCount: z.number().int().nonnegative().nullable(),
  }),
  /** What the purchase sheet draws (LIS-1173): the listing the access
   *  offers, its price, sale, questions and payment policy, compiled by the
   *  server. Null where the access offers no listing. The signed-in read
   *  fills its fields with the buyer's own answers; the public read never. */
  purchase: purchaseSchema.nullable(),
});

export const linkPageMusicAccessSchema = z.object({
  slug: artistLinkPageSlugSchema,
  artist: linkPageAccessArtistSchema,
  access: z.object({
    lineup: accessLineupSchema.optional(),
    type: z.literal("music"),
    id: z.string().min(1),
    artistId: z.string().min(1),
    title: z.string().min(1),
    artistName: z.string().min(1),
    artistImage: z.string().url().nullable(),
    extLinks: z.array(z.string()).nullable(),
    extSource: z.string().nullable(),
    unlocked: z.boolean(),
    /** For the artist's fan club: its current members own it (`unlocked`)
     *  and nobody unlocks it. Defaulted for payloads cached before. */
    membersOnly: z.boolean().default(false),
    /** Members-only music's club is its owner artist's: their name. */
    clubArtistName: z.string().nullable().default(null),
    price: z.number().nonnegative(),
    priceMin: z.number().nonnegative().nullable(),
    priceMax: z.number().nonnegative().nullable(),
    priceCurrency: z.string().min(1),
    collaborators: z
      .array(
        z.object({
          id: z.string().min(1),
          name: z.string().min(1),
          image: z.string().url().nullable(),
          slug: artistLinkPageSlugSchema.nullable(),
          /** Whether this artist has a published site, for a visitor on a
           *  site to be sent to it. Defaulted for payloads cached before. */
          hasSite: z.boolean().default(false),
        }),
      )
      .default([]),
    recommendations: z
      .array(
        z.object({
          id: z.string().min(1),
          slug: artistLinkPageSlugSchema,
          title: z.string().min(1),
          artistName: z.string().min(1),
          image: z.string().url().nullable(),
          isSetList: z.boolean().default(false),
          /** Whether its artist has a published site (see collaborators). */
          hasSite: z.boolean().default(false),
        }),
      )
      .default([]),
    songs: z
      .array(
        z.object({
          id: z.string().min(1),
          albums: z.array(
            z.object({
              id: z.string().min(1),
              title: z.string(),
              image: z.string(),
              trackNumber: z.number(),
            }),
          ),
          title: z.string().min(1),
          previewUrl: z.string().url().nullable(),
          duration: z.number().nonnegative(),
          isSetList: z.boolean().default(false),
          platformLinks: z
            .array(artistLinkPageSongPlatformLinkSchema)
            .default([]),
        }),
      )
      .min(1),
  }),
});

/**
 * A merch product an artist sells through a vendor's store (Music Glue),
 * bought here on the fan's behalf: the same access page and purchase sheet
 * as a vendor-listed event's, with the product's sizes as its tiers.
 */
export const linkPageMerchAccessSchema = z.object({
  slug: artistLinkPageSlugSchema,
  artist: linkPageAccessArtistSchema,
  access: z.object({
    type: z.literal("merch"),
    id: z.string().min(1),
    title: z.string().min(1),
    lineup: accessLineupDisplaySchema.optional(),
    description: z.string().nullable(),
    imageUrl: z.string().url().nullable(),
    /** The product's own page on its store; null when none is stored. */
    externalUrl: z.string().url().nullable(),
    /** `unavailable` when nothing can be bought: no live listing, or every
     *  size sold out. The page then says so rather than selling. `external`
     *  when Buy follows `externalUrl` to the artist's own store, with 1-tap
     *  off there. */
    checkoutMode: z.enum(["paid", "external", "unavailable"]),
    /** The cheapest size at quantity one, without UK delivery, which the
     *  page says beside it from the journey's delivery tiers. */
    price: z
      .object({
        amount: z.number().nonnegative(),
        currency: z.string().min(1),
        /** Sizes at different prices: render as "From …". */
        from: z.boolean().optional(),
      })
      .nullable(),
  }),
  /** What the purchase sheet draws (LIS-1173), as on an event. */
  purchase: purchaseSchema.nullable(),
});

export const linkPageAccessSchema = z.union([
  linkPageEventAccessSchema,
  linkPageMusicAccessSchema,
  linkPageMerchAccessSchema,
]);

export const linkPageMusicAccessOwnershipSchema = z.object({
  ownedAccessIds: z.array(z.string().min(1)),
});

/**
 * What the signed-in viewer already holds for the events on an artist's page:
 * tickets, tiers asked for rather than bought, Stay updated, and their fan
 * club ticket discount. Only events with something to show are listed.
 */
export const linkPageEventAccessViewerStatusSchema = z.object({
  accessId: z.string().min(1),
  ticketCount: z.number().int().nonnegative(),
  /** The address the tickets were issued to; null when none is held. */
  ticketEmail: z.string().nullable(),
  /** Opens the tickets on the event's Link in Bio tickets page
   *  (`/:slug/access/:accessId/tickets?token=`), as the ticket email's own
   *  link does; null when there is nothing that page can show. */
  ticketsToken: z.string().nullable().optional(),
  /** Open requests and waitlist places; `itemId` is the engine tier, null
   *  for a legacy event's single waitlist. */
  asked: z.array(
    z.object({
      itemId: z.string().nullable(),
      way: z.enum(["request", "waitlist"]),
      email: z.string(),
      /** The organiser approved it (or a place came up) and a checkout is
       *  held for them: the invitation went to `email`. */
      invited: z.boolean().optional(),
      invitationExpiresAt: z.string().nullable().optional(),
    }),
  ),
  /** The viewer pressed Stay updated on the event (it sold nothing then). */
  updatesRequested: z.boolean().optional(),
  /** The viewer's fan club ticket discount on the event (LIS-1177), as
   *  Lissen's copy of their membership says: the plan that gives it. The
   *  member price is the engine's, read for one event as its page opens
   *  (`linkPageMemberPricesSchema`). */
  memberDiscount: z.object({ planName: z.string() }).optional(),
});

export const linkPageEventAccessStatusSchema = z.object({
  accesses: z.array(linkPageEventAccessViewerStatusSchema),
});

/**
 * What the engine charges the signed-in member for one ticket of each tier of
 * one event they can buy now, where it prices it below the list price, by
 * engine item (LIS-1177), as its quote naming them said: the tickets sheet's
 * member price. Empty for a fan with no discount there.
 */
export const linkPageMemberPricesSchema = z.object({
  unitPriceMinor: z.record(z.string(), z.number().int().nonnegative()),
});

/**
 * A music unlock through the ticketing engine (LIS-1038): the same checkout
 * as a ticket, for one item. The page's unlock code (if it was opened with
 * one) makes it free; the consent is the "share my information with the
 * artist" tick.
 */
export const linkPageMusicUnlockRequestSchema = z.object({
  unlockCode: z.string().min(1).max(200).optional(),
  dataSharingConsent: z.boolean(),
  adContext: adContextSchema.optional(),
  // Reporting never refuses a purchase: one that does not parse is dropped.
  saleSource: saleSourceSchema.optional().catch(undefined),
  idempotencyKey: z.string().uuid(),
  // The client's purchase funnel (LIS-1110). Reporting never refuses a
  // purchase: one that does not parse is dropped.
  funnelId: z.string().uuid().optional().catch(undefined),
});

/** The engine checkout to pay in; or the music granted already (a free
 *  unlock, done at once); or the music the buyer already owned. */
export const linkPageMusicUnlockResponseSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("engine"),
    url: z.string().url(),
    clientSecret: z.string(),
  }),
  z.object({ kind: z.literal("unlocked") }),
  z.object({ kind: z.literal("owned") }),
]);

export const linkPageMusicUnlockConfirmRequestSchema = z.object({
  clientSecret: z.string().min(1).max(512),
});

/** Whether the music is the buyer's now: `pending` while its payment is
 *  still moving (the engine's webhook grants it when it lands). */
export const linkPageMusicUnlockConfirmResponseSchema = z.object({
  status: z.enum(["granted", "pending", "revoked"]),
});

export const linkPageAccessCheckoutRequestSchema = z.object({
  adContext: adContextSchema.optional(),
  // Reporting never refuses a purchase: one that does not parse is dropped.
  saleSource: saleSourceSchema.optional().catch(undefined),
  email: z.string().email().optional(),
  name: z.string().trim().max(200).nullable().optional(),
  phone: z.string().trim().max(40).nullable().optional(),
  ticketCount: z.number().int().positive(),
  lineItems: z
    .array(
      z.object({
        item_id: z.string(),
        quantity: z.number().int().positive().max(100),
      }),
    )
    .optional(),
  idempotencyKey: z.string().uuid().optional(),
  // The client's purchase funnel (LIS-1110). Reporting never refuses a
  // purchase: one that does not parse is dropped.
  funnelId: z.string().uuid().optional().catch(undefined),
});

export const linkPageAccessCheckoutResponseSchema = z
  .object({
    kind: z.literal("engine").optional(),
    url: z.string().url().optional(),
    checkoutUrl: z.string().url().optional(),
    clientSecret: z.string().min(1).optional(),
    publishableKey: z.string().min(1).optional(),
  })
  .refine((value) => value.kind !== "engine" || Boolean(value.url), {
    message: "Engine checkout requires a URL",
    path: ["url"],
  });

/** The engine refused the checkout for one of the event's checkout limits
 *  (LIS-924): the buyer's to fix, so the page says it in plain words from
 *  the engine's `code` and `detail` (its limit, and what is left). */
export const linkPageAccessCheckoutRefusedSchema = z.object({
  error: z.literal("checkout_refused"),
  refusal: z.object({
    code: z.string().min(1),
    // The limit refusals' detail is flat (event_id, limit, remaining).
    detail: z
      .record(z.string(), z.union([z.string(), z.number(), z.null()]))
      .nullable(),
  }),
});

export type LinkPageAccessCheckoutRefusal = z.infer<
  typeof linkPageAccessCheckoutRefusedSchema
>["refusal"];

export const stripePublishableKeySchema = z.object({
  publishableKey: z.string().min(1),
});

export const linkPageAccessRegisterResponseSchema = z.object({
  successUrl: z.string().min(1),
});

/**
 * A ticket on the event's tickets page, as the app's My Tickets lists it: an
 * engine ticket with its code (`qrPayload` is what its QR code holds), or
 * one bought on a seller's site (`fromSeller`), which has no code of ours
 * (`redeemCode` and `qrPayload` null) and names what was bought; how the
 * buyer gets it is the tickets read's `collect`.
 */
export const linkPageAccessTicketSchema = z.object({
  id: z.string().min(1),
  redeemCode: z.string().min(1).nullable(),
  qrPayload: z.string().min(1).nullable(),
  isWaitlist: z.boolean(),
  claimed: z.boolean(),
  fromSeller: z.boolean(),
  vendorBookingReference: z.string().nullable(),
  vendorTicketName: z.string().nullable(),
  vendorAttemptId: z.string().nullable(),
});

export const linkPageAccessTicketsSchema = z.object({
  slug: artistLinkPageSlugSchema,
  userEmail: z.string().email(),
  artist: z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    imageUrl: z.string().url().nullable(),
  }),
  access: linkPageEventAccessSchema.shape.access,
  tickets: z.array(linkPageAccessTicketSchema),
  /** My Tickets as the server compiles it for this viewer (`collectFor`):
   *  codes, documents and the account box only for the signed-in owner. */
  collect: collectOrNullSchema,
});

export type LinkPageAccess = z.infer<typeof linkPageAccessSchema>;
export type LinkPageEventAccess = z.infer<typeof linkPageEventAccessSchema>;
export type LinkPageMusicAccess = z.infer<typeof linkPageMusicAccessSchema>;
export type LinkPageMerchAccess = z.infer<typeof linkPageMerchAccessSchema>;
export type LinkPageMusicAccessOwnership = z.infer<
  typeof linkPageMusicAccessOwnershipSchema
>;
export type LinkPageEventAccessViewerStatus = z.infer<
  typeof linkPageEventAccessViewerStatusSchema
>;
export type LinkPageEventAccessStatus = z.infer<
  typeof linkPageEventAccessStatusSchema
>;
export type LinkPageMemberPrices = z.infer<typeof linkPageMemberPricesSchema>;
export type LinkPageMusicUnlockRequest = z.infer<
  typeof linkPageMusicUnlockRequestSchema
>;
export type LinkPageMusicUnlockResponse = z.infer<
  typeof linkPageMusicUnlockResponseSchema
>;
export type LinkPageMusicUnlockConfirmResponse = z.infer<
  typeof linkPageMusicUnlockConfirmResponseSchema
>;
export type LinkPageAccessCheckoutRequest = z.infer<
  typeof linkPageAccessCheckoutRequestSchema
>;
export type LinkPageAccessCheckoutResponse = z.infer<
  typeof linkPageAccessCheckoutResponseSchema
>;
export type StripePublishableKey = z.infer<typeof stripePublishableKeySchema>;
export type LinkPageAccessRegisterResponse = z.infer<
  typeof linkPageAccessRegisterResponseSchema
>;
export type LinkPageAccessTickets = z.infer<typeof linkPageAccessTicketsSchema>;
